'use client';

import {useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import {ChevronLeft, ChevronRight} from 'lucide-react';

type Product = {
    id: number;
    title: string;
    description: string;
    image: string;
    href: string;
};

export default function ProductCarousel({
                                            products,
                                            isRtl,
                                            viewDetailsLabel
                                        }: {
    products: Product[];
    isRtl: boolean;
    viewDetailsLabel: string;
}) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const AUTOPLAY_INTERVAL = 5000;

    const nextSlide = () => setCurrentSlide((p) => (p + 1) % products.length);
    const prevSlide = () => setCurrentSlide((p) => (p - 1 + products.length) % products.length);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (products.length > 1 && !isPaused) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((p) => (p + 1) % products.length);
            }, AUTOPLAY_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [currentSlide, products.length, isPaused]);

    return (
        <div className="overflow-hidden rounded-[20px] bg-white shadow-[0_4px_16px_rgba(15,23,42,.06)]">
            <div
                className="relative h-96 lg:h-96 bg-gradient-to-br from-[var(--sm-teal-wash)] to-white flex items-center justify-center overflow-hidden"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                {/* Slides */}
                <div className="relative w-full h-full overflow-hidden">
                    {products.map((product, idx) => (
                        <div
                            key={product.id}
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                                transform: (() => {
                                    const slideOffset = idx - currentSlide;
                                    if (slideOffset === 0) return 'translateX(0%)';
                                    if (slideOffset > 0) {
                                        return isRtl ? 'translateX(-100%)' : 'translateX(100%)';
                                    }
                                    return isRtl ? 'translateX(100%)' : 'translateX(-100%)';
                                })(),
                                transition: 'transform 500ms ease-in-out',
                                zIndex: idx === currentSlide ? 10 : 5 - Math.abs(idx - currentSlide),
                                pointerEvents: idx === currentSlide ? 'auto' : 'none'
                            }}
                        >
                            <div
                                className={`flex flex-col lg:flex-row items-center justify-evenly h-full gap-8 p-8`}
                            >
                                <Image
                                    src={product.image}
                                    alt={product.title}
                                    width={247}
                                    height={204}
                                    className="w-100 h-full object-cover rounded-lg shadow-md"
                                />
                                <div
                                    className={`flex flex-col h-full max-w-md text-center lg:text-left ${
                                        isRtl ? 'items-center lg:items-end lg:text-right' : 'items-center lg:items-start'
                                    }`}
                                >
                                    <div className="w-full">
                                        <h3 className="mb-3 text-3xl font-bold text-[var(--sm-ink)]">
                                            {product.title}
                                        </h3>
                                        <p className="text-gray-700 text-lg">{product.description}</p>
                                    </div>
                                    <div className="mt-auto flex w-full justify-end pt-6">
                                        <a
                                            href={product.href || "#"}
                                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--sm-ink)] transition hover:text-[var(--sm-teal-600)]"
                                        >
                                            {viewDetailsLabel}
                                            {isRtl ? <ChevronLeft size={16}/> : <ChevronRight size={16}/>}
                                        </a>
                                    </div>
                                </div>
                                <div>

                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Nav buttons */}
                <button
                    onClick={prevSlide}
                    className={`absolute ${isRtl ? 'right-4' : 'left-4'} top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-[13px] bg-white text-[var(--sm-ink)] shadow-[0_5px_16px_rgba(15,23,42,.09)] transition hover:scale-105 hover:text-[var(--sm-teal-600)]`}
                    aria-label="Previous"
                >
                    {isRtl ? <ChevronRight size={18}/> : <ChevronLeft size={16}/>}
                </button>
                <button
                    onClick={nextSlide}
                    className={`absolute ${isRtl ? 'left-4' : 'right-4'} top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-[13px] bg-white text-[var(--sm-ink)] shadow-[0_5px_16px_rgba(15,23,42,.09)] transition hover:scale-105 hover:text-[var(--sm-teal-600)]`}
                    aria-label="Next"
                >
                    {isRtl ? <ChevronLeft size={18}/> : <ChevronRight size={16}/>}
                </button>

                {/* Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {products.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentSlide(idx)}
                            className={`h-3 w-3 rounded-full transition-colors ${
                                idx === currentSlide ? 'bg-[var(--sm-teal)]' : 'bg-[rgba(15,23,42,.15)] hover:bg-[rgba(15,23,42,.3)]'
                            }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
